import { useMemo } from "react";
import { Chart } from "primereact/chart";
import {
  CHART_PALETTE,
  baseLegend,
  baseTooltip,
  formatMoney,
  formatNumber,
} from "./chartTheme";

interface DoughnutChartProps {
  labels: string[];
  values: number[];
  colors?: string[];
  money?: boolean;
  height?: number;
  // Text rendered in the doughnut hole (e.g. the total)
  centerLabel?: string;
  centerCaption?: string;
}

/**
 * Doughnut with share-of-total tooltip and optional centered total.
 */
export default function DoughnutChart({
  labels,
  values,
  colors,
  money = false,
  height = 280,
  centerLabel,
  centerCaption,
}: DoughnutChartProps) {
  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: labels.map(
            (_, i) => colors?.[i] ?? CHART_PALETTE[i % CHART_PALETTE.length],
          ),
          borderWidth: 2,
          borderColor: "#ffffff",
          hoverOffset: 6,
        },
      ],
    }),
    [labels, values, colors],
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      cutout: "62%",
      plugins: {
        legend: baseLegend,
        tooltip: {
          ...baseTooltip,
          callbacks: {
            label: (ctx: any) => {
              const total = (ctx.dataset.data as number[]).reduce(
                (acc, v) => acc + Math.abs(Number(v) || 0),
                0,
              );
              const share = total
                ? ((Math.abs(ctx.parsed) / total) * 100).toFixed(1)
                : "0";
              const value = money
                ? formatMoney(ctx.parsed)
                : formatNumber(ctx.parsed);
              return ` ${ctx.label}: ${value} (${share}%)`;
            },
          },
        },
      },
    }),
    [money],
  );

  // Draws the total in the middle of the chart area (legend-aware)
  const plugins = useMemo(
    () =>
      centerLabel
        ? [
            {
              id: "dgCenterLabel",
              afterDraw: (chart: any) => {
                const { ctx, chartArea } = chart;
                if (!chartArea) return;
                const x = (chartArea.left + chartArea.right) / 2;
                const y = (chartArea.top + chartArea.bottom) / 2;
                ctx.save();
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillStyle = "#1f2937";
                ctx.font = "700 16px sans-serif";
                ctx.fillText(centerLabel, x, centerCaption ? y - 8 : y);
                if (centerCaption) {
                  ctx.fillStyle = "#5b6474";
                  ctx.font = "400 11px sans-serif";
                  ctx.fillText(centerCaption, x, y + 11);
                }
                ctx.restore();
              },
            },
          ]
        : [],
    [centerLabel, centerCaption],
  );

  return (
    <div style={{ position: "relative", height, width: "100%" }}>
      <Chart
        type="doughnut"
        data={data}
        options={options}
        plugins={plugins}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );
}
